<script setup lang="ts">
import { useWebAppStore } from '@/stores/webApp'
import UserCard from '@/components/molecules/UserCard.vue'
import SideNavSkeleton from '@/components/molecules/SideNavSkeleton.vue'
import SideNavGroup from '@/components/molecules/SideNavGroup.vue'
import SideNavLink from '@/components/atoms/SideNavLink.vue'
import { useSideNav } from '@/composables/useSideNav'
import { useNavigation } from '@/composables/useNavigation'
import type { MenuEntry } from '@/types/navigation'

const webAppStore = useWebAppStore()
const { items, updateSelectedItem } = useNavigation()

const { isGroupOpen, isActive, isEntrySelected, onDetailToggle, setSubLevelRef, getEntryHref } =
  useSideNav()

const handleEntryClick = (entry: MenuEntry) => {
  updateSelectedItem(entry)
}
</script>

<template>
  <aside class="side-nav" :aria-busy="!webAppStore.isUserInfoReady">
    <SideNavSkeleton v-if="!webAppStore.isUserInfoReady" />

    <template v-else>
      <UserCard />
      <ul class="side-nav__list" data-testid="side-nav-list">
        <li
          v-for="entry in items"
          :key="entry.i18nLabel"
          class="side-nav__list-item"
          :class="[entry.className, { 'side-nav__list-item--selected': isEntrySelected(entry) }]"
        >
          <SideNavGroup
            v-if="entry.children?.length"
            :entry="entry"
            :is-open="isGroupOpen(entry.i18nLabel)"
            :is-active="isActive"
            :get-href="getEntryHref"
            @toggle="onDetailToggle"
            @click="handleEntryClick"
            @set-ref="setSubLevelRef"
          />
          <SideNavLink
            v-else
            :entry="entry"
            :is-active="isActive(entry)"
            :href="getEntryHref(entry)"
            :first-level="true"
            @click="handleEntryClick"
          />
        </li>
      </ul>
    </template>
  </aside>
</template>

<style lang="scss">
@use '@/styles/utils';

.side-nav {
  padding: ads.$ads-spacing-scale-lg ads.$ads-spacing-scale-lg ads.$ads-spacing-scale-2xl;
  display: flex;
  flex-direction: column;
  gap: ads.$ads-spacing-scale-2xl;
  background: ads.$ads-surface;
  border-right: 1px solid ads.$ads-outline-low;
  height: 100vh;

  &__list {
    display: flex;
    flex-direction: column;
    gap: ads.$ads-spacing-scale-2xl;
    list-style: none;
    padding: 0;
    margin: 0;
    margin-top: ads.$ads-spacing-scale-2xl;

    .side-nav__list-item:first-child {
      margin-top: 0;
    }
  }

  &__list-item {
    @include ads-sass.input-02;
    border-radius: ads.$ads-border-radius-container-low;
    border: 1px solid transparent;
    color: ads.$ads-on-surface-mid;

    &--selected-white {
      color: ads.$ads-accent;
    }
  }

  &__link-item {
    border-radius: ads.$ads-border-radius-container-low;
    border: 1px solid transparent;
  }

  &__skip-link {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;

    &:focus {
      position: static;
      width: auto;
      height: auto;
      overflow: visible;
      padding: ads.$ads-spacing-scale-sm ads.$ads-spacing-scale-lg;
      background: ads.$ads-loyalty-container-low;
      color: ads.$ads-on-surface-hi;
      text-decoration: underline;
      border-radius: ads.$ads-border-radius-container-low;
      z-index: 100;
    }
  }

  &__unlock-advantage {
    color: ads.$ads-on-loyalty-container-low;
    background-color: ads.$ads-loyalty-container-low;

    .ads-icon {
      transition: transform 0.1s ease;
      transform-origin: center;
      svg > * {
        fill: none;
        stroke: currentColor;
      }
    }

    &:hover {
      .ads-icon {
        transform: rotate(-10deg);
      }
    }
  }
}
</style>
